/**
 * Display-only order totals.
 * Mirrors backend order_service._compute_order_pricing and PurchaseForm.computeDisplayedPurchaseTotals
 * so the totals shown in the form match the backend's Decimal(0.01) quantization exactly.
 */
import type { OrderDiscountType, OrderDiscountScope } from "../types/models";

/** Form-level discount type includes the "none" sentinel used by the type toggle. */
export type OrderFormDiscountType = "none" | OrderDiscountType;

export interface OrderTotalsLine {
  quantity: number;
  unit_price: number;
  discount_type?: OrderFormDiscountType | null;
  discount_value?: number | null;
}

export interface OrderTotalsResult {
  subtotal: number;
  /** Order-level discount amount (only when scope === 'order'). */
  discountAmount: number;
  total: number;
  /** Per-line net totals (gross − line discount), same order as input lines. */
  lineTotals: number[];
  /** Per-line discount amounts (only when scope === 'line'). */
  lineDiscounts: number[];
}

/**
 * Round to 2 dp the same way the backend quantizes with Decimal("0.01").
 * Accepts number|string: catalog/order API responses serialize Decimal fields (unit_price,
 * cost_per_unit, ...) as JSON strings (e.g. "1100.00"), and a pristine line pre-filled straight
 * from the catalog carries that string through untouched until the user edits it. `Number.isFinite`
 * does not coerce strings, so without this coercion a never-retyped price silently quantizes to 0.
 */
export function quantizeMoney01(n: number | string): number {
  const num = Number(n);
  if (!Number.isFinite(num)) return 0;
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

function discountOnBase(base: number, type: OrderFormDiscountType | null | undefined, value: number): number {
  const v = Number(value);
  if (type === "percentage" && v > 0) return quantizeMoney01(base * (v / 100));
  if (type === "fixed" && v > 0) return quantizeMoney01(Math.min(v, base));
  return 0;
}

export function computeDisplayedOrderTotals(
  lines: OrderTotalsLine[],
  discountScope: OrderDiscountScope,
  orderDiscountType: OrderFormDiscountType,
  orderDiscountValue: number,
): OrderTotalsResult {
  const lineTotals: number[] = [];
  const lineDiscounts: number[] = [];
  let subtotal = 0;
  let sumLineDiscount = 0;

  for (const l of lines) {
    const gross = quantizeMoney01(Number(l.quantity) * Number(l.unit_price));
    const lineDisc =
      discountScope === "line" ? discountOnBase(gross, l.discount_type, Number(l.discount_value ?? 0)) : 0;
    const lineTotal = quantizeMoney01(Math.max(0, gross - lineDisc));
    lineTotals.push(lineTotal);
    lineDiscounts.push(lineDisc);
    subtotal += gross;
    sumLineDiscount += lineDisc;
  }
  subtotal = quantizeMoney01(subtotal);

  const discountAmount =
    discountScope === "order" ? discountOnBase(subtotal, orderDiscountType, Number(orderDiscountValue)) : 0;
  const total = quantizeMoney01(Math.max(0, subtotal - sumLineDiscount - discountAmount));

  return { subtotal, discountAmount, total, lineTotals, lineDiscounts };
}
